import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import fileDownload from 'js-file-download';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../../../utils/hooks/useQuery';
import utils from '../../../utils/utils';
import Api from '../../../api/api';
import classes from './delivery_sign.module.scss';
import { RangeDatePicker } from '../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import InputField from '../../_common/Form/TextFields/InputField';
import Button from '../../_common/Form/Buttons/Button';
import FilterButton from '../../_common/FilterButton/FilterButton';
import TableAndPagination from '../../_common/TableAndPagination/TableAndPagination';
import Loader from '../../_common/Loader/Loader';

const signedData = [
  { title: 'Signed', key: 'signedTrue', value: 'true' },
  { title: 'Not Signed', key: 'signedFalse', value: 'false' },
];

const collectRequestData = (requestData) => {
  const data = utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');

const DriverDeliveries = () => {
  const navigate = useNavigate();
  const { query, queryToStringify, setQuery } = useQuery();

  const [deliveries, setDeliveries] = useState([]);
  const [totalPage, setTotalPage] = useState(1);
  const [loading, setLoading] = useState('');

  const [requestData, setRequestData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    name: query.name || '',
    signed: query.signed || '',
    sortBy: query.sortBy || 'shipOn',
    sort: query.sort || 'asc',
    page: query.page || 1,
    limit: query.limit || 15,
  });

  const {
    page, limit, name, signed, sort, sortBy,
  } = requestData;

  const tableHeader = useMemo(() => [
    { name: 'Physician Name', path: 'physicianName', isSelectable: true },
    { name: 'Ship On', path: 'shipOn', isSelectable: true },
    { name: 'Address', path: 'address' },
    {
      name: 'Title',
      path: 'title',
      render: (d) => (
        <span
          className={classes.download_link}
          onClick={async (e) => {
            e.stopPropagation();
            const response = await fetch(d.link);
            const blob = await response.blob();
            fileDownload(blob, d.title);
          }}
        >
          {d.title}
        </span>
      ),
    },
    {
      name: 'Signed',
      path: 'signed',
      render: (d) => (
        <span className={d.signed === 'Yes' ? classes.signed_yes : classes.signed_no}>
          {d.signed}
        </span>
      ),
    },
    {
      name: 'Action',
      path: 'action',
      render: (d) => d.signed === 'No' && (
        <Button
          title="Sign"
          className={classes.sign_btn}
          onClick={() => navigate(`/deliveries/sign?attachmentId=${d.id}&patientId=${d.patientId}&physicianId=${d.physicianId}`, {
            state: queryToStringify(query),
          })}
        />
      ),
    },
  ], []);

  const getDriverDeliveries = async (params, loadingType = 'search') => {
    setLoading(loadingType);

    try {
      const { data } = await Api.getDriverDeliveries({
        page: params.page || 1,
        limit: params.limit || 15,
        start: params.start || undefined,
        end: params.end || undefined,
        name: params.name || undefined,
        signed: params.signed || undefined,
        sort: params.sort || undefined,
        sortBy: params.sortBy || undefined,
      });

      setDeliveries(data.result.map((item) => ({
        ...item,
        signed: item.signed ? 'Yes' : 'No',
      })));

      setTotalPage(data._meta?.pageCount || 1);
    } catch (e) {
      console.warn(e);
    }

    if (loadingType !== 'firstLoad') setQuery(params);
    setLoading('');
  };

  useEffect(() => {
    getDriverDeliveries(collectRequestData(requestData), 'firstLoad');
  }, []);

  const onChangeDate = (currentDate) => {
    setRequestData((prev) => ({ ...prev, start: currentDate[0], end: currentDate[1] }));
  };

  const changeSigned = (key, value) => {
    const newSigned = signed === value ? '' : value;
    setRequestData((prev) => ({ ...prev, signed: newSigned }));
  };

  const clearFilters = async () => {
    const defaultRequestData = {
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      name: '',
      signed: '',
      sortBy: 'shipOn',
      sort: 'asc',
      page: 1,
      limit: 15,
    };

    setRequestData(defaultRequestData);

    await getDriverDeliveries(defaultRequestData);
  };

  const onChangePageData = async (path, count) => {
    const newRequestData = { ...requestData, [path]: count };

    if (path === 'limit') {
      newRequestData.page = 1;
    }

    setRequestData(newRequestData);

    await getDriverDeliveries(collectRequestData(newRequestData), 'sort');
  };

  const onSortBy = async ({ sort: newSort, sortBy: newSortBy }) => {
    const newRequestData = {
      ...requestData, sort: newSort, sortBy: newSortBy, page: 1,
    };

    setRequestData(newRequestData);

    await getDriverDeliveries(collectRequestData(newRequestData), 'sort');
  };

  const submitFilters = async (e) => {
    e.preventDefault();

    const data = collectRequestData(requestData);
    data.page = 1;

    await getDriverDeliveries(data, 'search');
    setRequestData((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <div className={classes.deliveries_wrapper}>
      <form onSubmit={submitFilters} className={classes.filters_wrapper}>
        <div className={classes.filters}>
          <RangeDatePicker
            currentDate={requestData}
            onChangeDate={onChangeDate}
            showLastYear
          />

          <InputField
            label="Physician Name"
            wrapperClassName={classes.search}
            value={name}
            onChange={(value) => setRequestData((prev) => ({ ...prev, name: value }))}
          />

          <div className={classes.filter_button}>
            <FilterButton
              activeValue={signed}
              buttonsData={signedData}
              onClick={changeSigned}
            />
          </div>

          <Button
            className={classes.search_btn}
            title="Search"
            type="submit"
            loading={loading === 'search'}
          />

          <Button
            className={classes.clear_btn}
            title="Clear"
            clearButton
            onClick={clearFilters}
          />
        </div>
      </form>

      <div className={classes.table_wrapper}>
        {loading === 'firstLoad'
          ? <Loader />

          : (
            <TableAndPagination
              onChange={onChangePageData}
              data={deliveries}
              sort={sort}
              sortBy={sortBy}
              onSortBy={onSortBy}
              loading={loading === 'sort'}
              header={tableHeader}
              totalPage={totalPage}
              page={+page - 1}
              limit={+limit}
            />
          )}
      </div>
    </div>
  );
};

export default DriverDeliveries;
